import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2 } from 'lucide-react';
import { Reveal } from '../components/motion';
import SEO from '../components/SEO';
import { SectionTag, PageHero } from '../components/bits';
import { API, CONTACT, CONTACT_PATHS, CONTACT_INTERESTS } from '../content/data';

const ROLE_INTEREST = {
  student: 'Student Programmes', enterprise: 'Enterprise Membership', institution: 'Institutional Partnership',
  government: 'Government Partnership', media: 'Media', partner: 'Strategic Partnership',
};

export default function Contact() {
  const [params] = useSearchParams();
  const [interest, setInterest] = useState(ROLE_INTEREST[params.get('role')] || 'Student Programmes');
  const [form, setForm] = useState({ name: '', organisation: '', designation: '', email: '', phone: '', city: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    const r = params.get('role');
    if (r && ROLE_INTEREST[r]) setInterest(ROLE_INTEREST[r]);
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = 'w-full rounded-xl border border-navy/15 bg-white px-4 py-3.5 text-sm text-navy outline-none transition-colors focus:border-electric placeholder:text-navy/40';

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');
    try {
      await axios.post(`${API}/contact`, { ...form, interest });
      setStatus('done');
    } catch (err) {
      setStatus('idle');
      setError('Something went wrong. Please try again or email us directly.');
    }
  };

  const chooseRole = (role) => {
    setInterest(ROLE_INTEREST[role]);
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div data-testid="contact-page">
    <SEO title="Contact DITEC" description="Start the conversation — student, enterprise, institution, government, media or partner." />
      <PageHero
        ck="contact"
        tag="Contact DITEC"
        lines={<>Let's Start With What You're <span className="font-serif italic font-normal text-electric">Trying to Solve.</span></>}
        intro="Whether you're looking to learn, partner, hire, build, research or collaborate, tell DITEC what you're trying to achieve."
      />

      {/* Choose your path */}
      <section className="bg-paper py-16 sm:py-24">
        <div className="wrap">
          <SectionTag>01 — Choose Your Path</SectionTag>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CONTACT_PATHS.map((p, i) => (
              <Reveal key={p.role} delay={i * 0.05} className="flex flex-col rounded-3xl border border-navy/10 bg-white p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10">
                <h3 className="text-lg font-semibold tracking-tight">{p.title}</h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-navy/65">{p.desc}</p>
                <button
                  data-testid={`contact-path-${p.role}`}
                  onClick={() => chooseRole(p.role)}
                  className="mt-6 w-fit rounded-full bg-navy px-6 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-electric"
                >
                  {p.cta} →
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="contact-form" className="bg-white py-16 sm:py-24">
        <div className="wrap grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <SectionTag>02 — Start the Conversation</SectionTag>
            <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">Tell us what you're <span className="font-serif italic font-normal text-electric">trying to achieve.</span></h2></Reveal>
            <Reveal delay={0.1}>
              <div className="mt-10 space-y-4 rounded-3xl bg-navy p-8 text-white">
                <span className="overline !text-[10px] text-gold/70">Reach us directly</span>
                <a data-testid="contact-email" href={`mailto:${CONTACT.email}`} className="block text-lg font-semibold transition-colors hover:text-gold">{CONTACT.email}</a>
                <a data-testid="contact-phone" href={CONTACT.phoneHref} className="block font-mono text-base text-white/80 transition-colors hover:text-gold">+91 {CONTACT.phone}</a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            {status === 'done' ? (
              <div data-testid="contact-success" className="flex h-full flex-col items-center justify-center rounded-3xl border border-navy/10 bg-paper p-12 text-center">
                <CheckCircle2 className="h-12 w-12 text-electric" />
                <h3 className="text-h3 mt-5 font-semibold">Message received.</h3>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-navy/65">The DITEC team has your details and will respond shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form-element" className="rounded-3xl border border-navy/10 bg-paper p-6 sm:p-8">
                <div className="grid gap-3.5 sm:grid-cols-2">
                  <input data-testid="contact-name-input" required placeholder="Full Name" value={form.name} onChange={set('name')} className={inputCls} />
                  <input data-testid="contact-org-input" placeholder="Organisation" value={form.organisation} onChange={set('organisation')} className={inputCls} />
                  <input data-testid="contact-designation-input" placeholder="Designation" value={form.designation} onChange={set('designation')} className={inputCls} />
                  <input data-testid="contact-email-input" required type="email" placeholder="Email" value={form.email} onChange={set('email')} className={inputCls} />
                  <input data-testid="contact-phone-input" required type="tel" placeholder="Phone" value={form.phone} onChange={set('phone')} className={inputCls} />
                  <input data-testid="contact-city-input" placeholder="City / State" value={form.city} onChange={set('city')} className={inputCls} />
                </div>
                <select data-testid="contact-interest-select" value={interest} onChange={(e) => setInterest(e.target.value)} className={`${inputCls} mt-3.5`}>
                  {CONTACT_INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
                <textarea data-testid="contact-message-input" rows={4} placeholder="Tell us more" value={form.message} onChange={set('message')} className={`${inputCls} mt-3.5 resize-none`} />
                {error && <p data-testid="contact-error" className="mt-2 text-xs text-red-600">{error}</p>}
                <button data-testid="contact-submit-button" type="submit" disabled={status === 'loading'} className="mt-5 w-full touch-target rounded-full bg-electric py-4 text-sm font-semibold text-white transition-colors hover:bg-navy disabled:opacity-60">
                  {status === 'loading' ? 'Sending…' : 'Start the Conversation'}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
